/**
 * Viewpoint policy module.
 * Centralizes the thresholds that govern candidate search breadth, fallback rules,
 * and the coarse visibility cutoffs used throughout the planner.
 */
/**
 * Search-stage knobs that control how aggressively the planner explores nearby views.
 */
export interface ViewpointSearchPolicy {
  microYawOffsetsDeg: number[]
  outwardEscapeThreshold: number
  nudgeEscapeThreshold: number
}

/**
 * Decision-stage thresholds that decide when a fallback is better than the request.
 */
export interface ViewpointDecisionPolicy {
  preferTopPresentationOverride: boolean
  topPresentationCoverageThreshold: number
  topPresentationGainThreshold: number
  preferEdgeOutwardOverride: boolean
  edgePresentationThreshold: number
  edgePresentationMinCoverageRatio: number
  edgePresentationMaxOverlapPenalty: number
  edgePresentationMaxVisibleRatioDrop: number
  allowDegradedFallback: boolean
}

/**
 * Coarse visibility thresholds shared by the candidate scorer.
 */
export interface ViewpointVisibilityPolicy {
  clearVisibleSampleRatio: number
  clearOccluderOverlapRatio: number
  clearTargetCoverage: number
  usableVisibleSampleRatio: number
  usableOccluderOverlapRatio: number
  usableTargetCoverage: number
}

/**
 * Full policy bundle used by the search, decision, and visibility stages.
 */
export interface ViewpointPolicy {
  search: ViewpointSearchPolicy
  decision: ViewpointDecisionPolicy
  visibility: ViewpointVisibilityPolicy
}

/**
 * Partial policy overrides accepted from AI-authored viewpoint tools.
 */
export interface ViewpointPolicyOverrides {
  search?: Partial<ViewpointSearchPolicy>
  decision?: Partial<ViewpointDecisionPolicy>
  visibility?: Partial<ViewpointVisibilityPolicy>
}

/**
 * Tuned defaults chosen to keep requested views stable while still recovering
 * from severe occlusion or poor target framing.
 */
export const DEFAULT_VIEWPOINT_POLICY: ViewpointPolicy = {
  search: {
    microYawOffsetsDeg: [-15, 15],
    outwardEscapeThreshold: 0.35,
    nudgeEscapeThreshold: 0.05,
  },
  decision: {
    preferTopPresentationOverride: true,
    topPresentationCoverageThreshold: 0.04,
    topPresentationGainThreshold: 1.1,
    preferEdgeOutwardOverride: true,
    edgePresentationThreshold: 0.55,
    edgePresentationMinCoverageRatio: 0.5,
    edgePresentationMaxOverlapPenalty: 0.1,
    edgePresentationMaxVisibleRatioDrop: 0.05,
    allowDegradedFallback: true,
  },
  visibility: {
    clearVisibleSampleRatio: 0.75,
    clearOccluderOverlapRatio: 0.15,
    clearTargetCoverage: 0.03,
    usableVisibleSampleRatio: 0.55,
    usableOccluderOverlapRatio: 0.30,
    usableTargetCoverage: 0.015,
  },
}

/**
 * Merges tool-specific overrides with the default policy and clones mutable arrays
 * so downstream planner stages can treat the result as their local working config.
 */
export function resolveViewpointPolicy(overrides?: ViewpointPolicyOverrides): ViewpointPolicy {
  return {
    search: {
      ...DEFAULT_VIEWPOINT_POLICY.search,
      ...(overrides && overrides.search ? overrides.search : {}),
      microYawOffsetsDeg: overrides && overrides.search && overrides.search.microYawOffsetsDeg
        ? overrides.search.microYawOffsetsDeg.slice()
        : DEFAULT_VIEWPOINT_POLICY.search.microYawOffsetsDeg.slice(),
    },
    decision: {
      ...DEFAULT_VIEWPOINT_POLICY.decision,
      ...(overrides && overrides.decision ? overrides.decision : {}),
    },
    visibility: {
      ...DEFAULT_VIEWPOINT_POLICY.visibility,
      ...(overrides && overrides.visibility ? overrides.visibility : {}),
    },
  }
}
